import type { EmailSenderIdentity } from "@/features/material-compliance/api/inquiryEmailApi"
import type { InquiryTemplateSettings } from "@/features/material-compliance/components/supplier-declarations/inquiry/settings/templateSettingsMock"
import type { Organization } from "@/services/api/organizationApi"
import type { OrganizationSignature } from "@/services/api/organizationSignaturesApi"
import { languagesNeedingWriting, needsWriting } from "./placeholders"

// What each setup item checks. The checklist, the nav's "needs setup" dots
// and the badges on the settings pages all call these on the same queries,
// so they can never disagree about what is left to do.

/** Name and address: the letterhead of every document. */
export const companyDetailsNeedSetup = (org: Organization) =>
  !org.name.trim() || !org.addressMultiline?.trim()

export const logoNeedsSetup = (org: Organization) => !org.logoUrl

/** The address before the @ is free to choose until the first inquiry. */
export const sendingAddressNeedsSetup = (identity: EmailSenderIdentity) =>
  !identity.isSet

/** Per language: an empty signature sends the placeholder default. */
export const emailSignatureLanguagesNeedingSetup = (
  settings: InquiryTemplateSettings,
) => languagesNeedingWriting(settings.emailSignatures)

export const emailSignatureNeedsSetup = (settings: InquiryTemplateSettings) => {
  const languages = emailSignatureLanguagesNeedingSetup(settings)
  return languages.en || languages.de
}

/**
 * Whether the PDF footer can print at all: only the PDF-only questionnaire
 * has one. PDF + Excel prints the org's name and address there instead, and
 * Excel has no branding. An org that locked one of those never prints it.
 */
export const pdfFooterPrints = (settings: InquiryTemplateSettings) =>
  !settings.attachmentFormatLocked || settings.defaultAttachmentFormat === "pdf"

/** An empty footer prints the placeholder default too. */
export const pdfFooterNeedsSetup = (settings: InquiryTemplateSettings) =>
  pdfFooterPrints(settings) && needsWriting(settings.questionnaireFooter)

export const signatureImageNeedsSetup = (signatures: OrganizationSignature[]) =>
  signatures.length === 0
